/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  TrackingCustomerData,
  TrackingLocaleData,
  TrackingTransaction,
  TrackingTransactionItem,
} from '../../../types';
import {TrackingRecommendedSizes} from '../../../types/tracking-recommended-sizes';
import {TrackingVoucherData} from '../../../types/tracking-voucher-data';
import {WebTrackingAdditionalData} from '../additionalData';
import {
  DataLayerProductGA3,
  DataLayerProductGA4,
  EventPageData,
  TrackingClientStateHolder,
} from '../helpers/clientState';
import {DataLayerEvent} from '../helpers/datalayer';
import {exists} from '../helpers/exists';
import {
  FitAnalyticsDataLayerSummaryEvent,
  FitAnalyticsEvent,
  trackFitAnalyticsEvents,
} from '../helpers/fitAnalytics';
import {
  calcCentsToEuro,
  getSaleAmount,
  getTrackingPriceDataFromProduct,
} from '../helpers/prices';
import {
  MappedDimensionsProduct,
  createDataLayerProduct,
} from '../helpers/product';
import {stringifyEventData} from '../helpers/stringifyEventData';

export interface OrderSuccessPageTrackingEventGA4
  extends DataLayerEvent,
    EventPageData {
  ecommerce: {
    transaction_id: string;
    customer_id: string;
    value: string;
    sale_reduction_with_tax: string;
    campaign_reduction_with_tax: string;
    coupon_reduction_with_tax: string;
    tax: string;
    shipping: string;
    coupon: string;
    coupon_code: string;
    currency: string;
    payment_type: string;
    payment_split: string;
    payment_name: string;
    giftcard: string;

    items: DataLayerProductGA4[];
  };
  delivery_type: string;
}

const getPaymentName = ({transactions}: {transactions: TrackingTransaction}) =>
  stringifyEventData(transactions.payment.map(payment => payment.name));

const getPaymentType = ({transactions}: {transactions: TrackingTransaction}) =>
  stringifyEventData(transactions.payment.map(payment => payment.type));

const getPaymentSplit = ({transactions}: {transactions: TrackingTransaction}) =>
  stringifyEventData(
    transactions.payment.map(
      payment => `${payment.type}:${payment.totalWithTax}`
    )
  );

const getProductItems = ({
  transactions,
}: {
  transactions: TrackingTransaction;
  isGA4: boolean;
}): (MappedDimensionsProduct<DataLayerProductGA3> | DataLayerProductGA4)[] => {
  // eslint-disable-next-line complexity
  return transactions.items.map(item => {
    const {price, strikeOutPrice, tax} = getTrackingPriceDataFromProduct(
      item.variant.price
    );

    const additionalData = item.additionalData as {
      wt_sdk?: WebTrackingAdditionalData;
    };

    const {dataLayerProduct} = createDataLayerProduct({
      category:
        additionalData.wt_sdk &&
        additionalData.wt_sdk.category &&
        additionalData.wt_sdk.categoryId
          ? {
              path: additionalData.wt_sdk.category,
              id: additionalData.wt_sdk.categoryId,
            }
          : null,
      filter: additionalData.wt_sdk ? additionalData.wt_sdk.filter : '',
      fitAnalyticsUsed: additionalData.wt_sdk
        ? additionalData.wt_sdk.fitAnalyticsUsed
        : '',
      positionData:
        additionalData.wt_sdk &&
        exists(additionalData.wt_sdk.position) &&
        exists(additionalData.wt_sdk.sectionIndexOnPage) &&
        exists(additionalData.wt_sdk.rowIndexInSection) &&
        exists(additionalData.wt_sdk.columnIndexInSection)
          ? {
              position: additionalData.wt_sdk.position,
              sectionIndexOnPage: additionalData.wt_sdk.sectionIndexOnPage,
              rowIndexInSection: additionalData.wt_sdk.rowIndexInSection,
              columnIndexInSection: additionalData.wt_sdk.columnIndexInSection,
            }
          : undefined,
      price,
      product: item.product,
      unitPrice: item.product.unitPrice,
      quantity: item.selectedQuantity,
      saleAmount: getSaleAmount(item.product.minPrice),
      sort: additionalData.wt_sdk ? additionalData.wt_sdk.sort : '',
      sourceCategory:
        additionalData.wt_sdk &&
        additionalData.wt_sdk.sourceCategory &&
        additionalData.wt_sdk.sourceCategoryId
          ? {
              path: additionalData.wt_sdk.sourceCategory,
              id: additionalData.wt_sdk.sourceCategoryId,
            }
          : null,
      sourceHistory: {
        source: additionalData.wt_sdk ? additionalData.wt_sdk.source : '',
        sourceHistoryOne: additionalData.wt_sdk
          ? additionalData.wt_sdk.sourceHistoryOne
          : '',
        sourceHistoryTwo: additionalData.wt_sdk
          ? additionalData.wt_sdk.sourceHistoryTwo
          : '',
        sourceHistoryThree: additionalData.wt_sdk
          ? additionalData.wt_sdk.sourceHistoryThree
          : '',
      },
      strikeOutPrice: additionalData.wt_sdk
        ? additionalData.wt_sdk.strikeOutPrice ?? null
        : strikeOutPrice,
      tax,
      variantId: item.variant.id,
      supplierId: item.variant.supplierId,
      sizeLabel: item.variant.sizeLabel,
    });

    return dataLayerProduct;
  });
};

const getFitAnalyticsEvents = ({
  customer,
  transactions,
  recommendedSizes,
  localeData,
}: {
  customer: TrackingCustomerSubset;
  transactions: TrackingTransaction;
  recommendedSizes: TrackingRecommendedSizes;
  localeData: TrackingLocaleData;
}) => {
  const fitsRecommendedSize = (item: TrackingTransactionItem) =>
    exists(
      recommendedSizes.find(
        ({id, size}) =>
          id === item.product.id && size === item.variant.sizeLabel
      )
    );

  const doesntFitRecommmendedSize = (item: TrackingTransactionItem) =>
    exists(
      recommendedSizes.find(
        ({id, size}) =>
          id === item.product.id && size !== item.variant.sizeLabel
      )
    );

  const fitAnalyticsEvents: FitAnalyticsEvent[] = transactions.items
    .filter(fitsRecommendedSize)
    .map((item: TrackingTransactionItem) => {
      const {price} = getTrackingPriceDataFromProduct(item.variant.price);

      return {
        productSerial: `aboutyou-${item.product.id}`,
        orderId: String(transactions.orderId),
        userId: customer.type === 'existing' ? `${customer.id}` : '',
        purchasedSize: item.variant.sizeLabel!,
        shopArticleCode: item.variant.id,
        price,
        quantity: item.selectedQuantity,
        currency: transactions.total.currency,
        shopCountry: localeData.countryCode,
        language: localeData.languageCode,
        // We don't send these optional params:
        // shopSessionId: "2523818691",
        // ean: "0889516928738",
      };
    });

  const totalPrice = fitAnalyticsEvents.reduce(
    (all: number, event: FitAnalyticsEvent) => all + event.price,
    0
  );

  const productIds = transactions.items
    .map((item: TrackingTransactionItem) => item.product.id)
    .join(';');

  const productsWithFittingSize = fitAnalyticsEvents.length;
  const productsWithoutFittingSize = transactions.items.filter(
    doesntFitRecommmendedSize
  ).length;

  const dataLayerSummaryEvent: FitAnalyticsDataLayerSummaryEvent = {
    event: 'fa_data',
    fa_true: productsWithFittingSize,
    fa_false: productsWithoutFittingSize,
    fa_prods: productIds,
    value: totalPrice,
  };

  return {
    dataLayerSummaryEvent,
    fitAnalyticsEvents,
  };
};

interface TrackingCustomerSubset {
  type: TrackingCustomerData['type'];
  id: string | number;
}

export const trackOrderSuccessPage =
  ({
    customer,
    transactions,
    recommendedSizes,
    localeData,
    voucher,
    trackFitAnalytics = true,
    carriers,
  }: {
    customer: TrackingCustomerSubset;
    transactions: TrackingTransaction;
    recommendedSizes: TrackingRecommendedSizes;
    localeData: TrackingLocaleData;
    voucher?: TrackingVoucherData;
    trackFitAnalytics?: boolean;
    carriers?: string[];
  }) =>
  (clientState: TrackingClientStateHolder) => {
    const currentState = clientState.getCurrent();
    const {currentPage} = currentState;
    const eventGA4: OrderSuccessPageTrackingEventGA4 = {
      event: 'purchase',
      content_name: currentPage?.page?.path || '',
      page_type: currentPage?.page?.type || '',
      page_type_id:
        currentPage?.type === 'detailed' ? `${currentPage.page?.id || ''}` : '',
      ecommerce: {
        transaction_id: `${transactions.orderId}`,
        customer_id: customer.type === 'existing' ? `${customer.id}` : '',
        value: calcCentsToEuro(transactions.total.withoutTax).toFixed(2),
        sale_reduction_with_tax: calcCentsToEuro(
          transactions.total.saleReductionWithTax
        ).toFixed(2),
        campaign_reduction_with_tax: calcCentsToEuro(
          transactions.total.campaignReductionWithTax
        ).toFixed(2),
        coupon_reduction_with_tax: calcCentsToEuro(
          transactions.total.couponReductionWithTax
        ).toFixed(2),
        tax: calcCentsToEuro(
          transactions.total.withTax -
            transactions.total.withoutTax -
            (transactions.total.deliveryCostWithoutTax ?? 0)
        ).toFixed(2),
        shipping: calcCentsToEuro(
          transactions.total.deliveryCostWithoutTax || 0
        ).toFixed(2),
        coupon: voucher?.name || '',
        coupon_code: voucher?.code || '',
        currency: transactions.total.currency,
        payment_type: getPaymentType({transactions}),
        payment_name: getPaymentName({transactions}),
        payment_split: getPaymentSplit({transactions}),
        giftcard: '',

        items: getProductItems({
          transactions,
          isGA4: true,
        }) as DataLayerProductGA4[],
      },
      delivery_type: carriers?.join('||') || '',
    };

    const event = {
      GA4: eventGA4,
    };

    if (!trackFitAnalytics) {
      return event;
    }

    const {fitAnalyticsEvents, dataLayerSummaryEvent} = getFitAnalyticsEvents({
      customer,
      transactions,
      recommendedSizes,
      localeData,
    });

    trackFitAnalyticsEvents(fitAnalyticsEvents);

    return [event, dataLayerSummaryEvent];
  };
